import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDatoxtec } from "../../redux/actions";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Cell
} from "recharts";
import {
  FaChartPie,
  FaCalendarAlt,
  FaUserTie,
  FaFilter,
  FaUndo,
  FaCheckCircle,
  FaClock,
  FaTicketAlt,
  FaHourglassHalf,
  FaTools
} from "react-icons/fa";
import styles from "./Estadisxtec.module.css";

const DashboardTickets = ({ defaultAreaId }) => {
  const dispatch = useDispatch();
  const datosTecnicos = useSelector((state) => state.datxtec) || [];

  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState("Todos");
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("Todos");
  const [datosFiltrados, setDatosFiltrados] = useState([]);

  useEffect(() => {
    dispatch(getDatoxtec());
  }, [dispatch]);

  useEffect(() => {
    filtrarDatos();
  }, [datosTecnicos, fechaInicio, fechaFin, usuarioSeleccionado, estadoSeleccionado, defaultAreaId]);

  const filtrarDatos = () => {
    let datos = [...datosTecnicos];

    // Filtrar por área si se proporciona
    if (defaultAreaId) {
      datos = datos.filter(item => item.Usuario?.areaid === defaultAreaId);
    }

    if (fechaInicio) {
      datos = datos.filter(
        (item) => new Date(item.fecha) >= new Date(fechaInicio)
      );
    }

    if (fechaFin) {
      datos = datos.filter(
        (item) => new Date(item.fecha) <= new Date(fechaFin)
      );
    }

    if (usuarioSeleccionado !== "Todos") {
      datos = datos.filter(
        (item) =>
          item.Usuario &&
          item.Usuario.nombre &&
          item.Usuario.nombre.trim() === usuarioSeleccionado
      );
    }

    if (estadoSeleccionado !== "Todos") {
      if (estadoSeleccionado === "PorCerrar") {
        datos = datos.filter(
          (item) =>
            item.EstadoId === 4 &&
            item.Usuario &&
            !datos.some(
              (d) =>
                d.EstadoId === 5 &&
                d.Usuario &&
                d.Usuario.nombre === item.Usuario.nombre
            )
        );
      } else {
        datos = datos.filter(
          (item) => item.EstadoId === parseInt(estadoSeleccionado)
        );
      }
    }

    setDatosFiltrados(datos);
  };

  const obtenerDatosGrafico = () => {
    const agrupados = datosFiltrados.reduce((acc, item) => {
      const nombreUsuario = item.Usuario?.nombre?.trim() || "Sin usuario";

      if (!acc[nombreUsuario]) {
        acc[nombreUsuario] = {
          nombre: nombreUsuario,
          Resueltos: 0,
          Asignados: 0,
          Cerrados: 0,
          PorCerrar: 0,
        };
      }

      if (item.EstadoId === 4) acc[nombreUsuario].Resueltos += 1;
      if (item.EstadoId === 2) acc[nombreUsuario].Asignados += 1;
      if (item.EstadoId === 5) acc[nombreUsuario].Cerrados += 1;

      acc[nombreUsuario].PorCerrar =
        acc[nombreUsuario].Resueltos - acc[nombreUsuario].Cerrados;

      return acc;
    }, {});

    return Object.values(agrupados).sort((a, b) => (b.Resueltos + b.Asignados) - (a.Resueltos + a.Asignados));
  };

  const statsData = obtenerDatosGrafico();
  const totals = statsData.reduce((acc, curr) => ({
    asignados: acc.asignados + curr.Asignados,
    resueltos: acc.resueltos + curr.Resueltos,
    cerrados: acc.cerrados + curr.Cerrados,
    porCerrar: acc.porCerrar + curr.PorCerrar,
  }), { asignados: 0, resueltos: 0, cerrados: 0, porCerrar: 0 });

  const resetFiltros = () => {
    setFechaInicio("");
    setFechaFin("");
    setUsuarioSeleccionado("Todos");
    setEstadoSeleccionado("Todos");
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#fff', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <p style={{ fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, fontSize: '0.9rem', margin: '4px 0', fontWeight: 600 }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          <FaChartPie /> Dashboard de Gestión
        </h2>
        <p className={styles.subtitle}>Análisis detallado de rendimiento técnico y estado de tickets en tiempo real.</p>
      </header>

      <div className={styles.statsGrid}>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.yellow}`}><FaHourglassHalf /></div>
          <div className={styles.kpiInfo}>
            <h4>Tickets Asignados</h4>
            <p className={styles.kpiValue}>{totals.asignados}</p>
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.blue}`}><FaTools /></div>
          <div className={styles.kpiInfo}>
            <h4>Tickets Resueltos</h4>
            <p className={styles.kpiValue}>{totals.resueltos}</p>
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.green}`}><FaCheckCircle /></div>
          <div className={styles.kpiInfo}>
            <h4>Tickets Cerrados</h4>
            <p className={styles.kpiValue}>{totals.cerrados}</p>
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.red}`}><FaClock /></div>
          <div className={styles.kpiInfo}>
            <h4>Por Cerrar</h4>
            <p className={styles.kpiValue}>{totals.porCerrar}</p>
          </div>
        </div>
      </div>

      <div className={styles.filterCard}>
        <div className={styles.filterGroup}>
          <label><FaCalendarAlt /> Desde</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>

        <div className={styles.filterGroup}>
          <label><FaCalendarAlt /> Hasta</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>

        <div className={styles.filterGroup}>
          <label><FaUserTie /> Usuario Técnico</label>
          <select
            value={usuarioSeleccionado}
            onChange={(e) => setUsuarioSeleccionado(e.target.value)}
          >
            <option value="Todos">Todos los técnicos</option>
            {[
              ...new Set(
                datosTecnicos
                  .filter((item) => item.Usuario && item.Usuario.nombre && (!defaultAreaId || item.Usuario.areaid === defaultAreaId))
                  .map((item) => item.Usuario.nombre.trim())
              ),
            ].map((nombre) => (
              <option key={nombre} value={nombre}>
                {nombre}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label><FaFilter /> Estado Ticket</label>
          <select
            value={estadoSeleccionado}
            onChange={(e) => setEstadoSeleccionado(e.target.value)}
          >
            <option value="Todos">Todos los estados</option>
            <option value="2">Asignados</option>
            <option value="4">Resueltos</option>
            <option value="5">Cerrados</option>
            <option value="PorCerrar">Por Cerrar (Pendiente)</option>
          </select>
        </div>

        <button className={styles.resetButton} onClick={resetFiltros}>
          <FaUndo /> Borrar
        </button>
      </div>

      <div className={styles.chartSection}>
        <div className={styles.chartWrapper}>
          <h3 className={styles.chartTitle}>
            <FaTicketAlt color="#3b82f6" /> Distribución de Carga por Técnico
          </h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={statsData}
              margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
              barGap={10}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="nombre"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                angle={-45}
                textAnchor="end"
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '40px' }}
                iconType="circle"
              />

              <Bar dataKey="Asignados" fill="#ffc107" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Asignados" position="top" style={{ fill: '#d97706', fontSize: 11, fontWeight: 700 }} />
              </Bar>

              <Bar dataKey="Resueltos" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Resueltos" position="top" style={{ fill: '#1d4ed8', fontSize: 11, fontWeight: 700 }} />
              </Bar>

              <Bar dataKey="Cerrados" fill="#10b981" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Cerrados" position="top" style={{ fill: '#047857', fontSize: 11, fontWeight: 700 }} />
              </Bar>

              <Bar dataKey="PorCerrar" fill="#ef4444" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="PorCerrar" position="top" style={{ fill: '#b91c1c', fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardTickets;
